/**
 * Turns a NotificationTarget into actual navigation.
 *
 * Lives here rather than in `src/lib/hooks/` because it is part of the notification
 * domain, not a general-purpose data hook.
 *
 * Every destination reached here is a URL, not local component state, so a CTA lands on
 * something the user can copy and send to a colleague. That is also why the backend
 * sends ids and this file owns the URL shapes: rows outlive the URLs current when they
 * were written.
 */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { useEntitySheetParams } from "@/lib/hooks/use-entity-sheet-params";
import type { NotificationTarget } from "./target";

export interface UseNotificationNavigateOptions {
  /**
   * Opens the compose dialog for a contact id. `AppLayout` owns that dialog, so this
   * hook cannot open it directly — the one destination it has to hand back.
   */
  onComposeEmail?: (contactId: string) => void;
  /** Called after a target is handled, so the caller can close the popover. */
  onNavigated?: () => void;
}

export function useNotificationNavigate(opts: UseNotificationNavigateOptions = {}) {
  const { onComposeEmail, onNavigated } = opts;
  const navigate = useNavigate();
  const { openEntitySheet } = useEntitySheetParams();

  return useCallback(
    (target: NotificationTarget) => {
      switch (target.kind) {
        case "today": {
          const params = new URLSearchParams();
          if (target.tab) params.set("tab", target.tab);
          if (target.view) params.set("view", target.view);
          const query = params.toString();
          navigate(query ? `/?${query}` : "/");
          break;
        }

        case "person":
          openEntitySheet("personSheet", target.personId);
          break;

        case "company":
          openEntitySheet("companySheet", target.companyId);
          break;

        case "deal":
          openEntitySheet("dealSheet", target.dealId);
          break;

        case "task":
          // Same overlay mechanism as the entity sheets, so the CTA produces a URL a user
          // can copy — see the module note on why every destination is a URL.
          openEntitySheet("taskSheet", target.taskId);
          break;

        case "settings":
          navigate(
            target.section
              ? `/settings?section=${encodeURIComponent(target.section)}`
              : "/settings",
          );
          break;

        case "deals":
          // The Sunday alerts are about a SET of deals, so there is no id — the view is the
          // destination. `?list=` resolves a built-in deals view, and the alert views apply
          // exactly the DealFilter the digest counted with, scoped to the user's own deals.
          navigate(target.list ? `/deals?list=${encodeURIComponent(target.list)}` : "/deals");
          break;

        case "meeting": {
          // TodayPage owns the meeting deep-link: ?panel= selects the sheet and tab.
          const params = new URLSearchParams({
            meeting_id: target.meetingId,
            panel: target.panel,
          });
          navigate(`/?${params.toString()}`);
          break;
        }

        case "dealSuggestion": {
          // The exact URL the Slack digest already emits for the same suggestion, so
          // both channels resolve through one code path in TodayPage.
          const params = new URLSearchParams({
            meeting_id: target.meetingId,
            panel: "deals",
            deal_id: target.suggestionId,
          });
          navigate(`/?${params.toString()}`);
          break;
        }

        case "composeEmail":
          onComposeEmail?.(target.contactId);
          break;

        case "contactSuggestion":
          // The person sheet reads ?suggestion= and opens the review dialog over itself.
          openEntitySheet("personSheet", target.personId, { suggestion: target.suggestionId });
          break;

        case "none":
          // An informational row, or a target this build does not understand. Do
          // nothing rather than navigating somewhere arbitrary.
          return;
      }

      onNavigated?.();
    },
    [navigate, openEntitySheet, onComposeEmail, onNavigated],
  );
}
